import {
  Component,
  DestroyRef,
  ElementRef,
  NgZone,
  afterNextRender,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';

type CapabilityIcon = 'tag' | 'priority' | 'clone' | 'plus' | 'user-check' | 'search';
type TagTone = 'success' | 'info' | 'muted';

interface ListItem {
  mark: string;
  title: string;
  sub: string;
  tag: string;
  tone: TagTone;
}

type ResultCard =
  | {
      kind: 'fields';
      title: string;
      badge: string;
      fields: [string, string][];
      actions?: boolean;
    }
  | { kind: 'list'; items: ListItem[] }
  | { kind: 'table'; rows: [string, string, string, string, string][] };

interface Capability {
  label: string;
  icon: CapabilityIcon;
  description: string;
  thread: string;
  threadIndex: number;
  prompt: string;
  fitBubble?: boolean;
  steps: number;
  duration: string;
  doneLabel: string;
  intro: string;
  card: ResultCard;
  foot: string;
}

const PANEL_WIDTH = 734;
const TYPE_DELAY = 380;
const TYPE_SPEED = 22;
// How long a finished result stays on screen before the loop moves to the next feature.
const RESULT_HOLD = 2000;

@Component({
  selector: 'app-agentic-bot-demo',
  standalone: true,
  templateUrl: './agentic-bot-demo.html',
  styleUrl: './agentic-bot-demo.scss',
})
export class AgenticBotDemo {
  private readonly zone = inject(NgZone);
  private readonly stage = viewChild.required<ElementRef<HTMLElement>>('stage');
  private timers: ReturnType<typeof setTimeout>[] = [];
  private started = false;
  private reducedMotion = false;
  private pointerInside = false;
  private focusInside = false;
  private inView = false;
  private advancePending = false;

  readonly iconBase = '/assets/img/feature-imgs/itsm/agentic-bot/demo/';

  readonly capabilities: Capability[] = [
    {
      label: 'Incident Classification',
      icon: 'tag',
      description:
        'Every incoming incident is read and filed against your own category tree — no manual triage queue.',
      thread: 'Classify INC2043',
      threadIndex: 0,
      prompt: 'Classify INC2043 — “Outlook keeps disconnecting after the VPN drops”',
      steps: 3,
      duration: '200ms',
      doneLabel: 'Done: classify',
      intro: 'Classified against 18 months of resolved incident history:',
      card: {
        kind: 'fields',
        title: 'Classification result',
        badge: '94% confidence',
        fields: [
          ['Category', 'Network'],
          ['Subcategory', 'VPN / remote access'],
          ['Service', 'Corporate VPN'],
          ['Incident type', 'Client disconnect'],
        ],
      },
      foot: 'Applied to the ticket. Correct it once and the model learns the exception.',
    },
    {
      label: 'Incident Prioritization',
      icon: 'priority',
      description:
        'Impact and urgency are scored from the service catalog, so P1s surface before anyone escalates.',
      thread: 'Priority for INC2043',
      threadIndex: 1,
      prompt: 'What priority should INC2043 get?',
      steps: 4,
      duration: '300ms',
      doneLabel: 'Done: prioritize',
      intro: 'Scored on impact, urgency and the affected service tier:',
      card: {
        kind: 'fields',
        title: 'Priority recommendation',
        badge: 'P2 · High',
        fields: [
          ['Impact', '38 users · one site'],
          ['Urgency', 'High — work stopped'],
          ['Service tier', 'Tier 1 · business critical'],
          ['Resolution SLA', '4h — due 14:20 MYT'],
        ],
      },
      foot: 'Escalation to the network lead fires at 70% of the SLA window.',
    },
    {
      label: 'Similar Incident Retrieval',
      icon: 'clone',
      description:
        'The agent pulls the closest resolved incidents and their fixes before an engineer starts from zero.',
      thread: 'Similar to INC2043',
      threadIndex: 2,
      prompt: 'Show me similar incidents and how they were fixed',
      steps: 2,
      duration: '100ms',
      doneLabel: 'Done: retrieve',
      intro: 'Three close matches from the resolved archive:',
      card: {
        kind: 'list',
        items: [
          {
            mark: '96',
            title: 'VPN client drops on Wi-Fi roam',
            sub: 'INC1984 · resolved in 42m · split-tunnel policy push',
            tag: 'Resolved',
            tone: 'success',
          },
          {
            mark: '91',
            title: 'Outlook offline after tunnel reset',
            sub: 'INC1877 · resolved in 1h 10m · profile re-auth',
            tag: 'Resolved',
            tone: 'success',
          },
          {
            mark: '84',
            title: 'Gateway MTU mismatch, KL office',
            sub: 'INC1702 · closed · firmware rollback',
            tag: 'Closed',
            tone: 'muted',
          },
        ],
      },
      foot: 'Match score weighs symptoms, CI and site — not keywords alone.',
    },
    {
      label: 'Ticket Creation & Updates',
      icon: 'plus',
      description:
        'Raise, update and close tickets from the conversation, the agent confirms the payload before it writes.',
      thread: 'Raise VPN ticket',
      threadIndex: 5,
      prompt: 'Raise a ticket for the VPN login failure and set it to high',
      steps: 2,
      duration: '100ms',
      doneLabel: 'Done: create ticket',
      intro: 'I’m about to run Create Ticket. Reply “yes” to proceed or tell me what to change.',
      card: {
        kind: 'fields',
        title: 'Create ticket',
        badge: 'Pending approval',
        actions: true,
        fields: [
          ['Summary', 'Unable to login to VPN'],
          ['Service', 'Network / VPN access'],
          ['Priority', 'High · P2'],
          ['Requester', 'shalini.r'],
        ],
      },
      foot: 'Nothing is written to the ticket until you confirm.',
    },
    {
      label: 'Automated Ticket Assignment',
      icon: 'user-check',
      description:
        'Routing by skill, shift and live workload instead of a round-robin that ignores who is already buried.',
      thread: 'Assign INC2043',
      threadIndex: 3,
      prompt: 'Assign INC2043 to the right engineer',
      fitBubble: true,
      steps: 2,
      duration: '100ms',
      doneLabel: 'Done: assign',
      intro: 'Routed by skill match, shift roster and current open load:',
      card: {
        kind: 'list',
        items: [
          {
            mark: 'HK',
            title: 'Harish Kumar — Network L2',
            sub: '3 open · on shift until 18:00 · VPN certified',
            tag: 'Assigned',
            tone: 'success',
          },
          {
            mark: 'SR',
            title: 'Shalini Raj — Network L2',
            sub: '7 open · on shift · VPN certified',
            tag: 'Backup',
            tone: 'info',
          },
          {
            mark: 'KT',
            title: 'Kumar Thevar — Service desk',
            sub: '5 open · off shift from 14:00',
            tag: 'Skipped',
            tone: 'muted',
          },
        ],
      },
      foot: 'Reassignment rules respect on-call calendars and leave.',
    },
    {
      label: 'Ticket Information Lookup',
      icon: 'search',
      description:
        'Ask for any slice of the queue in plain language and get the live records back, not a saved report.',
      thread: 'Open requests',
      threadIndex: 4,
      prompt: 'Give me the request details which are in open state',
      steps: 2,
      duration: '100ms',
      doneLabel: 'Done: retrieve',
      intro: 'Here are your open service requests:',
      card: {
        kind: 'table',
        rows: [
          ['REQ1563', 'VPN Access Request', 'Open', 'Medium', 'harish10'],
          ['REQ1562', 'Antivirus request', 'Open', 'Medium', '—'],
          ['REQ1561', 'Unable to login to VPN', 'Open', 'High', 'shalini'],
          ['REQ1558', 'New laptop for onboarding', 'Open', 'Low', 'kumar.t'],
        ],
      },
      foot: 'Results respect the requester’s own permissions.',
    },
  ];

  private readonly baseThreads = [
    'Create a Support Ticket',
    'Printer Troubleshooting Assi…',
    'O365 Support Ticket Creation',
    'Active Service Requests Over…',
    'Request Details Inquiry',
    'New Chat Session',
  ];

  readonly step = signal(0);
  // Bumped on every run so the exchange block re-mounts and replays its entry transitions.
  readonly run = signal(0);
  readonly phase = signal(0);
  readonly typed = signal(0);
  readonly sendPressed = signal(false);
  readonly scale = signal<number | null>(null);

  readonly active = computed(() => this.capabilities[this.step()]);
  readonly runKey = computed(() => [this.run()]);

  readonly threads = computed(() => {
    const { thread, threadIndex } = this.active();
    const list = [...this.baseThreads];
    list.splice(threadIndex, 0, thread);
    return list;
  });

  readonly isTyping = computed(() => this.phase() === 0 && this.typed() > 0);

  readonly inputText = computed(() =>
    this.isTyping() ? this.active().prompt.slice(0, this.typed()) : 'Ask anything...',
  );

  readonly status = computed(() => {
    const { steps } = this.active();
    const phase = this.phase();
    if (phase < 3) return 'Thinking';
    if (phase < 4) return `1/${steps} steps · Running`;
    return `${steps}/${steps} steps · Completed`;
  });

  constructor() {
    const destroyRef = inject(DestroyRef);

    afterNextRender(() => {
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const stageEl = this.stage().nativeElement;

      this.zone.runOutsideAngular(() => {
        const resizeObserver = new ResizeObserver(([entry]) => {
          this.scale.set(Math.min(1, entry.contentRect.width / PANEL_WIDTH));
        });
        resizeObserver.observe(stageEl);

        const intersectionObserver = new IntersectionObserver(
          ([entry]) => {
            this.inView = entry.isIntersecting;
            if (this.inView && !this.started) {
              this.started = true;
              this.play(this.step());
            } else {
              this.resumeIfPending();
            }
          },
          { threshold: 0.25 },
        );
        intersectionObserver.observe(stageEl);

        destroyRef.onDestroy(() => {
          resizeObserver.disconnect();
          intersectionObserver.disconnect();
          this.clearTimers();
        });
      });
    });
  }

  select(index: number): void {
    if (index === this.step() && this.started) return;
    this.started = true;
    this.zone.runOutsideAngular(() => this.play(index));
  }

  setPointerInside(inside: boolean): void {
    this.pointerInside = inside;
    this.resumeIfPending();
  }

  onFocusIn(): void {
    this.focusInside = true;
  }

  onFocusOut(event: FocusEvent): void {
    // Tabbing between cards fires focusout before focusin; ignore moves that stay inside the grid.
    const grid = event.currentTarget as HTMLElement;
    if (grid.contains(event.relatedTarget as Node | null)) return;
    this.focusInside = false;
    this.resumeIfPending();
  }

  private get paused(): boolean {
    return this.pointerInside || this.focusInside || !this.inView;
  }

  private advance(): void {
    if (this.paused) {
      this.advancePending = true;
      return;
    }
    this.play((this.step() + 1) % this.capabilities.length);
  }

  private resumeIfPending(): void {
    if (!this.advancePending || this.paused) return;
    this.advancePending = false;
    this.zone.runOutsideAngular(() => this.advance());
  }

  private play(index: number): void {
    this.clearTimers();
    this.advancePending = false;
    this.step.set(index);
    this.run.update((n) => n + 1);
    this.sendPressed.set(false);

    if (this.reducedMotion) {
      this.typed.set(0);
      this.phase.set(7);
      return;
    }

    this.phase.set(0);
    this.typed.set(0);

    const length = this.capabilities[index].prompt.length;
    for (let c = 1; c <= length; c++) {
      this.at(TYPE_DELAY + c * TYPE_SPEED, () => this.typed.set(c));
    }

    const sent = TYPE_DELAY + length * TYPE_SPEED + 260;
    this.at(sent, () => {
      this.phase.set(1);
      this.sendPressed.set(true);
    });
    this.at(sent + 160, () => this.sendPressed.set(false));
    this.at(sent + 420, () => this.phase.set(2));
    this.at(sent + 900, () => this.phase.set(3));
    this.at(sent + 1500, () => this.phase.set(4));
    this.at(sent + 1750, () => this.phase.set(5));
    this.at(sent + 2050, () => this.phase.set(6));
    this.at(sent + 2600, () => this.phase.set(7));
    this.at(sent + 2600 + RESULT_HOLD, () => this.advance());
  }

  private at(ms: number, fn: () => void): void {
    this.timers.push(setTimeout(fn, ms));
  }

  private clearTimers(): void {
    this.timers.forEach(clearTimeout);
    this.timers = [];
  }
}
